import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Pin, Lock, ArrowLeft, MessageCircle, Trash2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import MentionTextarea from "@/components/MentionTextarea";
import CommentText from "@/components/CommentText";
import ForumBody from "@/components/ForumBody";
import BBCodeToolbar from "@/components/BBCodeToolbar";
import PostAuthorCard from "@/components/PostAuthorCard";
import { REACTIONS } from "@/components/reactions";
import { useAuth } from "@/contexts/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function ForumReactionRow({ targetId, targetKind, isOwn, reactions, myReactions, onChange }) {
  const { user } = useAuth();
  const handle = async (kind) => {
    if (!user) return toast.error("Sign in to react");
    if (isOwn) return toast.error("You can't react to your own post");
    try {
      const url = targetKind === "thread"
        ? `/forum_threads/${targetId}/react`
        : `/forum_posts/${targetId}/react`;
      const { data } = await api.post(url, { kind });
      onChange?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reaction failed");
    }
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {REACTIONS.map((r) => {
        const n = reactions?.[r.kind] || 0;
        const mine = (myReactions || []).includes(r.kind);
        return (
          <button
            key={r.kind}
            type="button"
            onClick={() => handle(r.kind)}
            disabled={isOwn}
            data-testid={`forum-react-${r.kind}-${targetId}`}
            title={`${r.label}${n ? ` · ${n}` : ""}`}
            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs border transition-transform hover:scale-105
              ${mine
                ? "bg-[var(--tt-primary)]/15 border-[var(--tt-primary)]/50 text-white"
                : "bg-white/[0.03] border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-white/30"}
              ${isOwn ? "opacity-60 cursor-default" : "cursor-pointer"}`}
          >
            <img src={r.url} alt="" className="w-4 h-4" />
            {n > 0 && <span className="font-mono-tt text-[10px]">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

function PostRow({ p, isOpening, thread, setThread, cards, posts, setPosts, user, isAdmin, togglePin, toggleLock, deletePost }) {
    const card = cards[p.user_id];
    const isOwn = user?.user_id === p.user_id;
    const updateReactions = (d) => {
      if (isOpening) {
        setThread((t) => ({ ...t, reactions: d.reactions, my_reactions: d.my_reactions, reactions_total: d.reactions_total }));
      } else {
        setPosts((prev) => prev.map((x) =>
          x.post_id === p.post_id ? { ...x, reactions: d.reactions, my_reactions: d.my_reactions, reactions_total: d.reactions_total } : x));
      }
    };
    return (
      <article
        data-testid={isOpening ? "thread-opening-post" : `forum-post-${p.post_id || p.thread_id}`}
        className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded overflow-hidden flex flex-col md:flex-row"
      >
        <PostAuthorCard card={card} />
        <div className="flex-1 min-w-0 p-5 flex flex-col">
          <header className="flex items-center gap-2 flex-wrap mb-2 text-xs text-[var(--tt-text-muted)]">
            {isOpening && thread.pinned && <Pin className="w-3.5 h-3.5 text-[var(--tt-yellow)]" />}
            {isOpening && thread.locked && <Lock className="w-3.5 h-3.5 text-red-400" />}
            {isOpening ? (
              <h1 className="font-display text-lg md:text-xl font-extrabold tracking-tight text-white">{thread.title}</h1>
            ) : null}
            <span className="ml-auto">Posted {timeAgo(p.created_at)}{p.edited_at ? " · edited" : ""}</span>
            {isOpening && isAdmin && (
              <>
                <button onClick={togglePin} data-testid="thread-admin-pin"
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border border-[var(--tt-border)] hover:border-[var(--tt-yellow)]/60">
                  {thread.pinned ? "Unpin" : "Pin"}
                </button>
                <button onClick={toggleLock} data-testid="thread-admin-lock"
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border border-[var(--tt-border)] hover:border-red-400/60">
                  {thread.locked ? "Unlock" : "Lock"}
                </button>
              </>
            )}
            {!isOpening && (isOwn || isAdmin) && (
              <button onClick={() => deletePost(p.post_id)} data-testid={`forum-post-delete-${p.post_id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] hover:text-red-400">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </header>
          <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-white/95 flex-1">
            <ForumBody
              text={isOpening ? thread.body : p.body}
              mentions={isOpening ? thread.mentions : p.mentions}
            />
          </div>
          <footer className="mt-4 pt-3 border-t border-[var(--tt-border)]">
            <ForumReactionRow
              targetId={isOpening ? thread.thread_id : p.post_id}
              targetKind={isOpening ? "thread" : "post"}
              isOwn={isOwn}
              reactions={p.reactions}
              myReactions={p.my_reactions}
              onChange={updateReactions}
            />
          </footer>
        </div>
      </article>
    );
}

export default function ThreadPage() {
  const { slug, threadId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const replyRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forum_threads/${threadId}`);
      setThread(data.thread);
      setPosts(data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load thread");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  // Batch-fetch author cards
  const allUserIds = useMemo(() => {
    const set = new Set();
    if (thread?.user_id) set.add(thread.user_id);
    for (const p of posts) if (p?.user_id) set.add(p.user_id);
    return Array.from(set);
  }, [thread, posts]);

  useEffect(() => {
    if (allUserIds.length === 0) return;
    const missing = allUserIds.filter((u) => !cards[u]);
    if (missing.length === 0) return;
    api.get("/users/cards", { params: { ids: missing.join(",") } })
      .then(({ data }) => setCards((prev) => ({ ...prev, ...data })))
      .catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUserIds.join(",")]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/forum_threads/${threadId}/posts`, { body: reply.trim() });
      setPosts((prev) => [...prev, data]);
      setReply("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reply failed");
    } finally { setPosting(false); }
  };

  const deletePost = async (post_id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/forum_posts/${post_id}`);
      setPosts((prev) => prev.filter((p) => p.post_id !== post_id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const isAdmin = user && (user.role === "admin" || user.role === "mod");
  const togglePin = async () => {
    try {
      const { data } = await api.post(`/admin/forum_threads/${threadId}/pin`);
      setThread((t) => ({ ...t, pinned: data.pinned }));
    } catch { toast.error("Failed"); }
  };
  const toggleLock = async () => {
    try {
      const { data } = await api.post(`/admin/forum_threads/${threadId}/lock`);
      setThread((t) => ({ ...t, locked: data.locked }));
    } catch { toast.error("Failed"); }
  };

  /** PostRow is extracted above to keep component identity stable across renders. */

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          <Header />
          <main className="px-4 md:px-8 py-6 max-w-5xl">
            <Link to={`/forums/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-[var(--tt-text-muted)] hover:text-white mb-3">
              <ArrowLeft className="w-3 h-3" /> Back to forum
            </Link>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--tt-text-muted)] py-10">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : !thread ? (
              <div>Thread not found.</div>
            ) : (
              <div className="space-y-4">
                {/* Opening post (thread body) */}
                <PostRow p={thread} isOpening thread={thread} setThread={setThread} cards={cards}
                  posts={posts} setPosts={setPosts} user={user} isAdmin={isAdmin}
                  togglePin={togglePin} toggleLock={toggleLock} deletePost={deletePost} />

                {posts.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-[var(--tt-text-muted)] pt-2">
                    <MessageCircle className="w-3.5 h-3.5 text-[var(--tt-purple)]" />
                    {posts.length} {posts.length === 1 ? "reply" : "replies"}
                  </div>
                )}

                {posts.map((p) => (
                  <PostRow key={p.post_id} p={p} isOpening={false} thread={thread} setThread={setThread}
                    cards={cards} posts={posts} setPosts={setPosts} user={user} isAdmin={isAdmin}
                    togglePin={togglePin} toggleLock={toggleLock} deletePost={deletePost} />
                ))}

                {thread.locked ? (
                  <div className="text-center text-xs text-[var(--tt-text-muted)] py-6 border border-dashed border-[var(--tt-border)] rounded">
                    🔒 This thread is locked.
                  </div>
                ) : !user ? (
                  <div className="text-center text-xs text-[var(--tt-text-muted)] py-6 border border-dashed border-[var(--tt-border)] rounded">
                    <Link to="/login" className="text-[var(--tt-primary)] font-bold">Sign in</Link> to reply.
                  </div>
                ) : (
                  <form onSubmit={submit} className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded p-3">
                    <BBCodeToolbar textareaRef={replyRef} value={reply} onChange={(e) => setReply(e.target.value)} />
                    <MentionTextarea
                      ref={replyRef}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Write a reply... [b]bold[/b], [quote]quote[/quote], or @mention"
                      rows={4}
                      maxLength={8000}
                      data-testid="thread-reply-textarea"
                      className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-y font-mono-tt"
                    />
                    <div className="flex items-center justify-end mt-2">
                      <button
                        type="submit"
                        disabled={posting || !reply.trim()}
                        data-testid="thread-reply-submit"
                        className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded disabled:opacity-50"
                      >
                        {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Post Reply
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
